import React, { forwardRef, useState, useCallback } from 'react';
import styled, { css } from 'styled-components';

import TextInput from '@components/dataEntry/Input/TextInput';
import Button from '@components/dataEntry/button/Button';
import SwapIconSec from '@pages/Setting/3.Homepage/common/SwapIconSec';
import CustomUploadBox from '@components/dataEntry/upload/CustomUploadBox';
import { ACCESS_TYPE } from '@typings/common/accessType';
import { useInputs } from '@hooks/useInputs';
import { deleteImg } from '@store/firebase/storage';
import { getEditPagePath } from '@utils/getPath';
import { editWorldMenu } from '@store/firebase/world/update-world';
import { deleteWorldDoc } from '@store/firebase/world/delete-world';

const dragItemStyle = css`
  border-radius: var(--cardBorderRadius);
  background-color: var(--cardBackgroundColor);
  border: 0;
`;

const ItemWrapper = styled.div<{
  isDrag?: boolean;
}>`
  padding: 0 12px 0 32px;
  margin-bottom: 4px;
  box-sizing: border-box;
  width: 100%;
  color: var(--textColor);
  background-color: var(--cardBackgroundColor);
  border-radius: var(--cardBorderRadius);
  border-bottom: 1px solid var(--lineColor);
  transition: background-color 0.2s;
  position: relative; // swap icon 고정시키기용

  display: grid;
  grid-template-columns: 0.4fr 1fr auto auto;
  align-items: center;
  gap: 12px;

  ${({ isDrag }) => isDrag && dragItemStyle}
`;

const TitleSec = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 8px 0;
`;

type Props = {
  data?: any;
  style?: React.CSSProperties;
  listeners?: any;
  attributes?: any;
};

function menuOrderItem({ data, style, ...props }: Props, ref) {
  const [selectedAccessKey, setSelectedAccessKey] = useState(
    data ? data.accessType : ACCESS_TYPE.PUBLIC,
  );
  const collectionPath = getEditPagePath();
  const [isSaving, setIsSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [{ menuValue }, handleChange, resetInput] = useInputs({
    menuValue: data.menuValue || '',
  });

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    const fileList = { selectedFile, prevImgURL: data ? data.imgURL : null };
    const newData = {
      menuValue,
    };
    await editWorldMenu({
      data: newData,
      docId: data.id,
      fileList,
      collectionPath,
    });
    setIsSaving(false);
  }, [menuValue, selectedAccessKey, data, selectedFile, collectionPath]);

  const handleDeleteBoard = useCallback(() => {
    if (confirm('메뉴를 정말 삭제하시겠습니까?')) {
      deleteWorldDoc({ imgURL: data.imgURL, boardId: data.id, collectionPath });
    } else {
      return;
    }
  }, [data, collectionPath]);

  const handleDeleteImage = useCallback(() => {
    if (data)
      deleteImg({
        imgURL: data.imgURL,
        docId: data.id,
        key: 'imgURL',
        collectionPath,
      });
  }, [data, collectionPath]);

  if (!data) return null;

  return (
    <ItemWrapper>
      <SwapIconSec {...props} />

      <TitleSec>
        {data.title}
        <TextInput
          name={'menuValue'}
          value={menuValue}
          onChange={handleChange}
          placeholder="메뉴명"
        />
      </TitleSec>

      <Box>
        <CustomUploadBox
          id={`menu-icon-${data.id}`}
          setSelectedFile={setSelectedFile}
          prevImgURL={data?.imgURL}
          text="이미지 추가"
          customDelete={handleDeleteImage}
          label="이미지 (최적 200 * 80)"
          style={{ minWidth: 180, height: 60 }}
        />
      </Box>

      <Box>
        <Button onClick={handleSave} size="md" pending={isSaving}>
          저장
        </Button>
        <Button onClick={handleDeleteBoard} size="md" shape="stroke">
          삭제
        </Button>
      </Box>
    </ItemWrapper>
  );
}

export default forwardRef(menuOrderItem);
